import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { clsx, withSpacing } from '@wds/utils';
import { SEMANTIC_COLORS, SIZES } from '@wds/helpers';
import { useTheme } from 'react-jss';

import useStyles from './useStyles';

export const buttonTypes = ['button', 'reset', 'submit'];
export const variants = ['solid', 'ghost', 'text'];
export const buttonColors = ['alt', 'link', ...SEMANTIC_COLORS];

const Button = ({
  ariaPressed,
  buttonType,
  children,
  color,
  disabled,
  href,
  label,
  size,
  stretched,
  variant,
  className,
  ...rest
}) => {
  const [pressed, setPressed] = useState(ariaPressed);
  useEffect(() => {
    if (ariaPressed !== pressed) setPressed(!pressed);
  }, [ariaPressed, pressed]);

  const numberOfChildren = React.Children.count(children);
  const firstChildType = React.Children.toArray(children)[0].type;
  const lastChildType =
    React.Children.toArray(children)[numberOfChildren - 1].type;

  const iconOnly =
    numberOfChildren === 1 &&
    firstChildType &&
    firstChildType.displayName &&
    firstChildType.displayName.includes('WithIcon');

  const leftIcon =
    numberOfChildren > 1 &&
    firstChildType &&
    firstChildType.displayName &&
    firstChildType.displayName.includes('WithIcon');

  const rightIcon =
    numberOfChildren > 1 &&
    lastChildType &&
    lastChildType.displayName &&
    lastChildType.displayName.includes('WithIcon');

  // https://mathiasbynens.github.io/rel-noopener/
  const autoAddRel = rest.target && rest.target === '_blank' && !rest.rel;
  const rel = autoAddRel && 'noopener noreferrer';

  const theme = useTheme();
  const { wrenchButton } = useStyles({
    color,
    variant,
    size,
    pressed,
    stretched,
    theme,
    iconOnly,
    leftIcon,
    rightIcon,
  });

  const classNames = clsx(
    className,
    wrenchButton,
    // Static class used for internal reference
    // Ex. ButtonGroup conditional styles
    `wrenchButton--${variant}`
  );

  /* eslint-disable react/button-has-type */
  return (
    <>
      <Choose>
        <When condition={href}>
          <a
            {...rest}
            className={classNames}
            href={href}
            title={label}
            rel={rel}
          >
            {children}
          </a>
        </When>
        <Otherwise>
          <button
            {...rest}
            className={classNames}
            type={buttonType}
            disabled={disabled}
            aria-pressed={ariaPressed}
            aria-label={label}
          >
            {children}
          </button>
        </Otherwise>
      </Choose>
    </>
  );
};

Button.defaultProps = {
  ariaPressed: false,
  buttonType: 'button',
  className: undefined,
  color: 'primary',
  disabled: false,
  href: undefined,
  label: undefined,
  size: 'md',
  stretched: false,
  variant: 'solid',
};

Button.propTypes = {
  /**
   * Aria pressed state of Button
   */
  ariaPressed: PropTypes.bool,
  /**
   * Type attribute of button element
   */
  buttonType: PropTypes.oneOf(buttonTypes),
  /**
   * Text, icons for Button
   */
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  /**
   * Set background color of the button
   */
  color: PropTypes.oneOf(buttonColors),
  /**
   * If true button will be disabled
   */
  disabled: PropTypes.bool,
  /**
   * Use if the button is a link to a url.
   */
  href: PropTypes.string,
  /**
   * Label for title or aria-label depending on the type of button (<a> or <button> element).
   */
  // eslint-disable-next-line consistent-return
  label: (props, propName) => {
    // we only need to see if it's an Icon since other cases could be valid
    const searchTerm = 'WithIcon';
    let typeCheck = false;
    // props.children could be an array or not
    if (Array.isArray(props.children)) {
      typeCheck = props.children.some(
        (child) =>
          child.type &&
          child.type.displayName &&
          child.type.displayName.indexOf(searchTerm) > -1
      );
    } else {
      typeCheck =
        props.children.type &&
        props.children.type.displayName &&
        props.children.type.displayName.indexOf(searchTerm) > -1;
    }

    if (props[propName] === undefined && typeCheck) {
      return new Error(
        'A label is required when the child of a Button is a React component'
      );
    }
  },
  /**
   * Size variants for Button
   */
  size: PropTypes.oneOf(SIZES),
  /**
   * Stretch the width of the button to match its container
   */
  stretched: PropTypes.bool,
  /**
   * Choose what button style variant to display.
   */
  variant: PropTypes.oneOf(variants),
};

const ButtonWithSpacing = withSpacing(Button);

ButtonWithSpacing.displayName = 'Button';

export default ButtonWithSpacing;
